import type { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@/components/ui/confirm-dialog';
import { toast } from '@/components/ui/toast';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsTogActive } from '@/hooks/useIsTogActive';
import { useShipToIntraWorkflow, useShipWorkflow } from '@/services/workflows';

export type ShippingWorkflowValidation = () => {
  isValid: boolean;
  message?: string;
};

export function useShippingWorkflowMenu(
  ids: string | string[],
  onWorkflowExecute?: () => void,
  workflowValidation?: ShippingWorkflowValidation,
): MenuItem[] {
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');
  const isUserValidationEnabled = useFeatureFlag('Workflow_User_Validation');
  const isTogActive = useIsTogActive();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [ship] = useShipWorkflow();
  const [shipToIntra] = useShipToIntraWorkflow();

  // INTRA storage locations exist only in the TOG database.
  const canShipToIntra = !isUserValidationEnabled || isTogActive;
  const workflows = [
    { label: t('workflow.names.Ship'), execute: ship },
    ...(canShipToIntra
      ? [{ label: t('workflow.names.ShipToIntra'), execute: shipToIntra }]
      : []),
  ];

  return workflows.map(({ label, execute: runNative }) => ({
    label,
    icon: 'pi pi-play-circle',
    async command() {
      const validation = workflowValidation?.();

      if (validation && !validation.isValid) {
        toast.add({
          data: { variant: 'error' },
          title: t('workflow.toast.restrictionTitle'),
          description:
            validation.message || t('workflow.toast.restrictionDetail'),
          timeout: 10000,
        });
        return;
      }

      const shipmentIds = Array.isArray(ids) ? ids : [ids];
      const execute = async () => {
        try {
          await runNative(
            { ids: shipmentIds },
            { onFinished: onWorkflowExecute },
          );
        } catch (error) {
          // The service owns submission and terminal notifications.
          console.error('Shipping workflow error:', error);
        }
      };

      if (confirmWorkflows) {
        const count = shipmentIds.length;
        confirm({
          title: 'Confirm Workflow',
          description: (
            <>
              Run <b>{label}</b> workflow for {count} shipment
              {count === 1 ? '' : 's'}?
            </>
          ),
          confirmLabel: 'OK',
          cancelLabel: 'Cancel',
          onConfirm: execute,
        });
      } else {
        await execute();
      }
    },
  }));
}
